import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EmptyState, Button, Spinner } from "@fd/ui";

// Keep the installed app's first paint lean. Each route is fetched when it is
// opened and then retained by the service worker for later launches.
const LandingRedirect = lazy(() => import("./routes/LandingRedirect").then((m) => ({ default: m.LandingRedirect })));
const Login = lazy(() => import("./routes/Login").then((m) => ({ default: m.Login })));
const AuthCallback = lazy(() => import("./routes/AuthCallback").then((m) => ({ default: m.AuthCallback })));
const SetPassword = lazy(() => import("./routes/SetPassword").then((m) => ({ default: m.SetPassword })));
const AppShell = lazy(() => import("./routes/AppShell").then((m) => ({ default: m.AppShell })));
const OrgDashboard = lazy(() => import("./routes/OrgDashboard").then((m) => ({ default: m.OrgDashboard })));
const OrgContracts = lazy(() => import("./routes/OrgContracts").then((m) => ({ default: m.OrgContracts })));
const OrgSubscriptions = lazy(() => import("./routes/OrgSubscriptions").then((m) => ({ default: m.OrgSubscriptions })));
const OrgInvoices = lazy(() => import("./routes/OrgInvoices").then((m) => ({ default: m.OrgInvoices })));
const OrgOffers = lazy(() => import("./routes/OrgOffers").then((m) => ({ default: m.OrgOffers })));
const OrgMembers = lazy(() => import("./routes/OrgMembers").then((m) => ({ default: m.OrgMembers })));
const OrgSettings = lazy(() => import("./routes/OrgSettings").then((m) => ({ default: m.OrgSettings })));
const OfferView = lazy(() => import("./routes/OfferView").then((m) => ({ default: m.OfferView })));
const OfferSign = lazy(() => import("./routes/OfferSign").then((m) => ({ default: m.OfferSign })));
const OfferCounter = lazy(() => import("./routes/OfferCounter").then((m) => ({ default: m.OfferCounter })));
const QuoteBuilder = lazy(() => import("./routes/QuoteBuilder").then((m) => ({ default: m.QuoteBuilder })));

const SupportArticleLink = lazy(() => import("./routes/SupportArticleLink").then(m => ({ default: m.SupportArticleLink })));
const SupportRequestLink = lazy(() => import("./routes/SupportRequestLink").then(m => ({ default: m.SupportRequestLink })));
const PortalAuth = lazy(() => import("./routes/PortalAuth").then(m => ({ default: m.PortalAuth })));
const SupportHome = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportHome })));
const SupportArticle = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportArticle })));
const SupportRequests = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportRequests })));
const SupportNewRequest = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportNewRequest })));
const SupportRequestDetail = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportRequestDetail })));
const SupportPortal = lazy(() => import("./routes/SupportPortal").then(m => ({ default: m.SupportPortal })));
const SupportEntry = lazy(() => import('./routes/SupportEntry').then(m => ({default:m.SupportEntry})));
const PublicSupportArticle = lazy(() => import('./routes/PublicSupport').then(m => ({default:m.PublicSupportArticle})));

function IndexLoading() {
  const { t } = useTranslation();
  return (
    <div className="fd-centered">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <Spinner />
        <span className="fd-body">{t("states.loading")}</span>
      </div>
    </div>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="fd-centered">
      <EmptyState
        icon="search"
        title={t("notFound.title")}
        description={t("notFound.description")}
        action={<Button variant="gold" onClick={() => window.location.assign("/app")}>{t("notFound.action")}</Button>}
      />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<IndexLoading />}>
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/portal" element={<PortalAuth />} />
        <Route path="/auth/portal/complete" element={<PortalAuth complete />} />
        <Route path="/servicedesk/customer/portal/:deskId/article/:sourceId" element={<SupportArticleLink />} />
        <Route path="/support/request/:jiraKey" element={<SupportRequestLink />} />
        <Route path="/servicedesk/customer/portal/:deskId/:jiraKey" element={<SupportRequestLink />} />
        <Route path="/support" element={<SupportEntry />} />
        <Route path="/support/articles/:slug" element={<PublicSupportArticle />} />
        <Route path="/support/workspace" element={<SupportPortal />} />
        <Route path="/support/org/:orgId" element={<SupportPortal />}>
          <Route index element={<SupportHome />} />
          <Route path="/support/org/:orgId/articles/:slug" element={<SupportArticle />} />
          <Route path="/support/org/:orgId/requests" element={<SupportRequests />} />
          <Route path="/support/org/:orgId/requests/new" element={<SupportNewRequest />} />
          <Route path="/support/org/:orgId/requests/:requestId" element={<SupportRequestDetail />} />
        </Route>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/set-password" element={<SetPassword />} />
        <Route path="/quote" element={<QuoteBuilder />} />
        <Route path="/quote/:family" element={<QuoteBuilder />} />
        <Route path="/offers/:id" element={<OfferView />} />
        <Route path="/offers/:id/sign" element={<OfferSign />} />
        <Route path="/offers/:id/counter" element={<OfferCounter />} />
        <Route path="/app" element={<AppShell />}>
          <Route index element={<IndexLoading />} />
          <Route path="org/:orgId" element={<OrgDashboard />} />
          <Route path="org/:orgId/contracts" element={<OrgContracts />} />
          <Route path="org/:orgId/subscriptions" element={<OrgSubscriptions />} />
          <Route path="org/:orgId/invoices" element={<OrgInvoices />} />
          <Route path="org/:orgId/offers" element={<OrgOffers />} />
          <Route path="org/:orgId/members" element={<OrgMembers />} />
          <Route path="org/:orgId/settings" element={<OrgSettings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
